import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ResendBulkPasteModal } from "../components/ResendBulkPaste";
import { api } from "@/lib/api";
import { useEkycChatSummary } from "@/components/EkycChatButton";
import {
  MessageSquare,
  MessageSquareText,
  ShieldCheck,
  Flame,
  Inbox,
  ClipboardPaste,
  Wallet,
  LogOut,
  ArrowRight,
  Send,
  Landmark,
  KeyRound,
  CalendarClock,
  BadgeCheck,
  Truck,
  HandCoins,
} from "lucide-react";

// =============================================================================
// Home hub — single landing page after login. Big tile shortcuts so admin
// doesn't have to retype URLs / get stuck on /admin when they need /chat.
// =============================================================================
export default function Home() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const ekycChat = useEkycChatSummary();
  const isPrivileged = isAdmin || user?.auto_join_groups || user?.is_owner;
  // Bulk-resend paste modal — admin one-tap tile on this page. Uses local
  // state instead of a route because the flow is self-contained (paste →
  // preview → submit) and doesn't need its own URL.
  const [bulkResendOpen, setBulkResendOpen] = useState(false);
  // Live "Ready today" count for the Cooling Tracker tile badge. Admin-only
  // (endpoint is admin-gated). Non-admin logins just skip the fetch.
  const [coolingReadyToday, setCoolingReadyToday] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cooling/stats");
        if (!cancelled) setCoolingReadyToday(data?.ready_today || 0);
      } catch {
        /* non-fatal — badge just stays 0 */
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);
  const [myReady, setMyReady] = useState(null);
  const [pdCount, setPdCount] = useState(null);
  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      api.get("/pending-deliveries", { params: { date: "all" } }),
      api.get("/pending-deliveries", { params: { date: "all", ignore_enabled: true } }),
    ]).then(([on, all]) => setPdCount({ on: on.data?.totals?.pending || 0, all: all.data?.totals?.pending || 0 })).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    if (isAdmin) return;
    api.get("/cooling/my-agencies").then(({ data }) => setMyReady(data?.ready_today || 0)).catch(() => setMyReady(0));
  }, [isAdmin]);

  const tiles = [
    {
      id: "chat",
      title: "Chat",
      desc: "Group messages, send LPG codes, see live complaints.",
      icon: MessageSquare,
      color: "bg-[#385C4B]",
      onClick: () => navigate("/chat"),
      visible: true,
    },
    {
      id: "admin",
      title: "Admin Panel",
      desc: "Users, groups, staff sheets, backups, integrations.",
      icon: ShieldCheck,
      color: "bg-[#1C1C19]",
      onClick: () => navigate("/admin"),
      visible: isAdmin,
    },
    {
      id: "complaints",
      title: "Complaint Box",
      desc: "Saari pending complaints date-wise — view & resolve.",
      icon: Inbox,
      color: "bg-[#C8553D]",
      onClick: () => navigate("/complaints"),
      visible: true,
    },
    {
      id: "lpg-report",
      title: "LPG Report",
      desc: "Daily ledger of sent / done / pending / billable codes.",
      icon: Flame,
      color: "bg-[#A86F2E]",
      onClick: () => navigate("/lpg-report"),
      visible: true,
    },
    {
      id: "bank-details",
      title: "Bank Details",
      desc: "Active accounts + QR codes + daily deposit sheet.",
      icon: Landmark,
      color: "bg-[#385C4B]",
      onClick: () => navigate("/bank-details"),
      visible: true,
    },
    {
      id: "bulk-paste",
      title: "Bulk Paste Dispatcher",
      desc: "Multi-agency paste — auto route DAC codes to each group.",
      icon: ClipboardPaste,
      color: "bg-[#385C4B]",
      onClick: () => navigate("/bulk-paste"),
      visible: isPrivileged,
    },
    {
      id: "bulk-resend",
      title: "Bulk Resend Paste",
      desc: "Bharat Gas only · rows staff-attributed, agency posted real-time.",
      icon: Send,
      color: "bg-[#8A6046]",
      onClick: () => setBulkResendOpen(true),
      visible: isAdmin,
    },
    {
      id: "wallet",
      title: "Wallet & Ledger",
      desc: "Agency + staff ledger, rate master, settlements — admin only.",
      icon: Wallet,
      color: "bg-[#8A6046]",
      onClick: () => navigate("/admin/wallet"),
      visible: isAdmin,
    },
    {
      id: "payouts",
      title: "Payout Manager",
      desc: "Bookers, salesman, developer · kamai, payment, balance · Excel/PDF.",
      icon: HandCoins,
      color: "bg-[#385C4B]",
      onClick: () => navigate("/payouts"),
      visible: isAdmin,
    },
    {
      id: "sms-inbox",
      title: "Messages · SMS Inbox",
      desc: "Phone ke Google Messages se codes auto save · ek click copy.",
      icon: MessageSquareText,
      color: "bg-[#2F6F8F]",
      onClick: () => navigate("/sms-inbox"),
      visible: isAdmin,
    },
    {
      id: "delivery-logins",
      title: "Delivery Logins",
      desc: "Indane agencies ki delivery-man user id · password one page.",
      icon: KeyRound,
      color: "bg-[#385C4B]",
      onClick: () => navigate("/delivery-logins"),
      visible: isAdmin,
    },
    {
      id: "cooling-tracker",
      title: "Cooling Tracker",
      desc: "Upload cooling CSV · auto-promote consumers to booking-ready on their date.",
      icon: CalendarClock,
      color: "bg-[#C8553D]",
      onClick: () => navigate("/cooling-tracker"),
      visible: isAdmin,
      badge: coolingReadyToday > 0 ? `${coolingReadyToday} ready today` : null,
    },
    {
      id: "pending-deliveries",
      title: "Pending Deliveries",
      desc: "Saari agencies ki pending delivery · ek jagah, agency-wise.",
      icon: Truck,
      color: "bg-[#8A6046]",
      onClick: () => navigate("/pending-deliveries"),
      visible: isAdmin,
      badge: pdCount === null ? null : pdCount.on === pdCount.all ? `${pdCount.on} pending` : `${pdCount.on} ON · ${pdCount.all} total`,
    },
    {
      id: "ekyc",
      title: "EKYC",
      desc: "Indane / Bharat / HPCL ka KYC kaam · staff hisab · report.",
      icon: BadgeCheck,
      color: "bg-[#2F6F8F]",
      onClick: () => navigate("/admin?tab=ekyc"),
      visible: isAdmin,
      actions: [
        { id: "manage", label: "Manage", to: "/admin?tab=ekyc" },
        { id: "desk", label: "Desk", to: "/ekyc-desk" },
      ],
    },
    {
      id: "ekyc-chat",
      title: "EKYC Chat",
      desc: "Aapke EKYC groups · messages, files aur records dekho.",
      icon: BadgeCheck,
      color: "bg-[#2F6F8F]",
      onClick: () => navigate("/ekyc-desk?view=chat"),
      visible: !isAdmin && (ekycChat?.groups || 0) > 0,
      badge: ekycChat?.unread ? `${ekycChat.unread} unread` : null,
    },
    {
      id: "my-cooling",
      title: "My Cooling",
      desc: "Kis date me kitne consumer ready honge · refill unblock IDs.",
      icon: CalendarClock,
      color: "bg-[#C8553D]",
      onClick: () => navigate("/my-cooling"),
      visible: !isAdmin,
      badge: myReady === null ? null : `${myReady} ready`,
    },
    {
      id: "agency-ledger",
      title: "My Agency Ledger",
      desc: "Deliveries · payments · adjustments · balance.",
      icon: Wallet,
      color: "bg-[#8A6046]",
      onClick: () => navigate("/agency-ledger"),
      // Only shown when admin has explicitly granted this user access via
      // Admin Panel → Users → "Can view own ledger". Keeps ledger data
      // private by default even from agency members.
      visible: !isAdmin && user?.can_view_ledger === true,
    },
  ].filter((t) => t.visible);

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="home-page">
      <header className="bg-[#1C1C19] text-[#F5F5F0] px-6 py-4 flex items-center justify-between">
        <div
          onClick={() => navigate("/home")}
          className="flex items-center gap-3 cursor-pointer"
          data-testid="home-brand"
        >
          <div className="w-9 h-9 rounded-md bg-[#385C4B] flex items-center justify-center font-bold">
            X
          </div>
          <div>
            <div className="text-[9px] font-mono tracking-[0.3em] uppercase text-[#F5F5F0]/60">
              Non Stop Chatting
            </div>
            <div
              className="text-lg font-semibold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              XYZ Chatting
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#F5F5F0]/60">
              {(user?.role === "admin" ? "Admin" : "Member") + (user?.is_owner ? " · Owner" : "")}
            </div>
            <div
              className="text-sm font-semibold"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {user?.display_name || user?.username}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="home-logout-button"
            className="p-2 rounded hover:bg-white/10 transition-colors"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#6B6B61]">
          Home
        </div>
        <h1
          className="text-4xl font-semibold tracking-tight text-[#1C1C19] mt-2"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Welcome back{user?.display_name ? `, ${user.display_name}` : ""}
        </h1>
        <p className="text-sm text-[#6B6B61] mt-2">
          Choose where to go next. Top-left logo ko kabhi bhi click karke yahan
          wapas aa sakte hain.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={t.onClick}
                data-testid={`home-tile-${t.id}`}
                className="group bg-white border border-[#DCDCD3] hover:border-[#385C4B] rounded-lg p-5 text-left shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className={`${t.color} text-white w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-base font-semibold text-[#1C1C19] flex items-center gap-2"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {t.title}
                      {t.badge && (
                        <span className="text-[10px] font-mono bg-red-600 text-white px-1.5 py-0.5 rounded-full">
                          {t.badge}
                        </span>
                      )}
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-[#385C4B]" />
                    </div>
                    {t.desc && (
                      <div className="text-xs text-[#6B6B61] mt-1 leading-relaxed">
                        {t.desc}
                      </div>
                    )}
                    {t.actions && (
                      <div className="flex gap-2 mt-3">
                        {t.actions.map((a) => (
                          <span
                            key={a.id}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); navigate(a.to); }}
                            data-testid={`home-tile-${t.id}-${a.id}`}
                            className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded border border-[#2F6F8F] text-[#2F6F8F] hover:bg-[#2F6F8F] hover:text-white transition-colors"
                          >
                            {a.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>
      {bulkResendOpen && (
        <ResendBulkPasteModal
          onClose={() => setBulkResendOpen(false)}
          onCommitted={() => setBulkResendOpen(false)}
          title="Home · Bulk Resend Paste (Bharat Gas)"
          subtitle="Attribute rows to their sub-staff in one paste"
        />
      )}
    </div>
  );
}
